import { eventTracker } from 'app/modules/Segment';
import {
  FeatureEnum,
  GroupIdEnum,
  OwnerEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';
import { MENU_VERSION_VALUES } from 'app/services/userPreferences/types';

const EVENTS = {
  GAMIFICATION_PAGE_VIEWED: 'gamification_page_viewed',
  GAMIFICATION_INVITE_MODAL_OPENED: 'gamification_invite_modal_opened',
  GAMIFICATION_JUSFY_PAY_REDIRECT: 'gamification_jusfy_pay_redirect',
  MENU_GAMIFICATION_DETAILS_VIEWED: 'menu_gamification_details_viewed',
  MENU_GAMIFICATION_DETAILS_CLICKED: 'menu_gamification_details_clicked',
  GAMIFICATION_MGM_CTA_CLICKED: 'gamification_mgm_cta_clicked',
  GAMIFICATION_MGM_LINK_COPIED: 'gamification_mgm_link_copied',
} as const;

const GAMIFICATION_LIBRARY_NAME = 'gamification-library';
const GAMIFICATION_FEATURE = FeatureEnum.GAMIFICATION;

function buildGamificationProperties(
  eventText: string,
  relevantUserActivity: boolean,
  eventMeaning: string,
  overrides: Partial<TProperties> = {},
): TProperties {
  return {
    owner: OwnerEnum.INOVACAO,
    feature: GAMIFICATION_FEATURE,
    event_meaning: eventMeaning,
    relevant_user_activity: relevantUserActivity,
    event_text: eventText,
    ...overrides,
  } as TProperties;
}

function buildContext(componentName: string): TContext {
  return {
    groupId: GroupIdEnum.GAMIFICATION_GROUP,
    library: {
      name: GAMIFICATION_LIBRARY_NAME,
      componente: componentName,
    },
  };
}

export function trackGamificationPageViewed(): void {
  eventTracker.track({
    event: EVENTS.GAMIFICATION_PAGE_VIEWED,
    properties: buildGamificationProperties(
      'Gamification Page Viewed',
      false,
      'Disparado quando a página de Gamification é exibida ao usuário com a feature habilitada.',
    ),
    context: buildContext('gamification-page'),
  });
}

export function trackGamificationInviteModalOpened(props: {
  challengeId?: string | number | null;
  challengeName?: string | null;
  challengePoints?: number | null;
}): void {
  eventTracker.track({
    event: EVENTS.GAMIFICATION_INVITE_MODAL_OPENED,
    properties: buildGamificationProperties(
      'Gamification Invite Modal Opened',
      true,
      'Disparado quando o modal de convite de um desafio de indicação é aberto na Gamification. Propriedades: `challenge_id`, `challenge_name`, `challenge_points`.',
      {
        challenge_id: props.challengeId ?? null,
        challenge_name: props.challengeName ?? null,
        challenge_points: props.challengePoints ?? null,
      },
    ),
    context: buildContext('challenges-list-component'),
  });
}

export function trackGamificationJusfyPayRedirect(props: {
  challengeId?: string | number | null;
  challengeName?: string | null;
  challengeEvent?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.GAMIFICATION_JUSFY_PAY_REDIRECT,
    properties: buildGamificationProperties(
      'Gamification Jusfy Pay Redirect',
      true,
      'Disparado quando o usuário inicia um desafio especial que redireciona para o JusfyPay na Gamification. Propriedades: `challenge_id`, `challenge_name`, `challenge_event`.',
      {
        challenge_id: props.challengeId ?? null,
        challenge_name: props.challengeName ?? null,
        challenge_event: props.challengeEvent ?? null,
      },
    ),
    context: buildContext('challenges-list-component'),
  });
}

export function trackMenuGamificationDetailsViewed(): void {
  eventTracker.track({
    event: EVENTS.MENU_GAMIFICATION_DETAILS_VIEWED,
    properties: buildGamificationProperties(
      'Menu Gamification Details Viewed',
      false,
      'Disparado quando o usuário abre o dropdown de gamificação no novo menu. Propriedades: `feature`, `menu_version`.',
      {
        menu_version: MENU_VERSION_VALUES.v2,
      },
    ),
    context: buildContext('drawer-component'),
  });
}

export function trackGamificationMgmCtaClicked(): void {
  eventTracker.track({
    event: EVENTS.GAMIFICATION_MGM_CTA_CLICKED,
    properties: buildGamificationProperties(
      'Gamification Mgm Cta Clicked',
      true,
      'Disparado quando o usuário clica em Copiar no bloco MGM da página de Gamification.',
    ),
    context: buildContext('mgm-block-component'),
  });
}

export function trackGamificationMgmLinkCopied(): void {
  eventTracker.track({
    event: EVENTS.GAMIFICATION_MGM_LINK_COPIED,
    properties: buildGamificationProperties(
      'Gamification Mgm Link Copied',
      true,
      'Disparado quando o link de indicação é copiado com sucesso pelo usuário no bloco MGM da página de Gamification.',
    ),
    context: buildContext('mgm-block-component'),
  });
}

export function trackMenuGamificationDetailsClicked(): void {
  eventTracker.track({
    event: EVENTS.MENU_GAMIFICATION_DETAILS_CLICKED,
    properties: buildGamificationProperties(
      'Menu Gamification Details Clicked',
      true,
      'Disparado quando o usuário clica em “Ver mais desafios” no dropdown de gamificação do novo menu. Propriedades: `feature`, `menu_version`.',
      {
        menu_version: MENU_VERSION_VALUES.v2,
      },
    ),
    context: buildContext('button-component'),
  });
}
